import { createContext, useContext, useMemo } from 'react';
import type React from 'react';
import { VehicleTag } from '~/contracts/Vehicle';
import AvailabilityInfo from '../contracts/AvailabilityInfo';

type SoonestAvailabilityInfoType = Partial<{
  activityType: string;
  dayAvailable: string;
  dateAvailable: string;
  daysTillDeliveryOrPickup: number;
  locationCity: string;
  locationState: string;
  routeId: string;
}>;

export type TileData = {
  locationId: number;
  soonestAvailabilityInfo?: SoonestAvailabilityInfoType;
  deliveryInfo?: AvailabilityInfo;
  imageUrl: string;
  isFavorite?: boolean;
  isSimilarTile?: boolean;
  isOnDemand: boolean;
  jellyBeanMobileUrl: string;
  make: string;
  mileage: number;
  model: string;
  price: {
    apr: number;
    discounts: Array<{
      amount: number;
      type: string;
    }>;
    downPayment: number;
    downPaymentDetails: {
      totalCashDue: number;
      additionalCashDown: number;
      nonRefundableShippingFees: number;
      tradeDown: number;
    };
    estimatedTaxesAndFees?: number;
    feesMinusShipping: number;
    kbbValue: number;
    monthlyPayment: number;
    pricingErrorMessage?: string;
    term: number;
    total: number;
    totalFees: number;
    totalTaxes: number;
    totalDiscountAmount: number;
    transportCost: number;
    tradeDown: number;
  };
  previousPrice?: number;
  stockNumber: number;
  stockRecallStatusType: number;
  storeKey: string;
  transportCost: number;
  trim: string;
  vehicleId: number;
  vehicleInventoryType: number;
  vehiclePurchaseType: string;
  vehicleTags: VehicleTag[];
  vehicleUnlockDateTime?: string;
  vin: string;
  year: number;
};

type TileDataContextType = {
  tileData: Partial<TileData>;
};

const defaultContext: TileDataContextType = {
  tileData: {}
};

const TileDataContext = createContext<TileDataContextType>(defaultContext);

export function useTileDataContext() {
  return useContext(TileDataContext);
}

export const TileDataContextProvider: React.FC<{ tileData: TileData }> = ({
  children,
  tileData
}) => {
  const value = useMemo<TileDataContextType>(() => ({ tileData }), [tileData]);

  return (
    <TileDataContext.Provider value={value}>
      {children}
    </TileDataContext.Provider>
  );
};

export default TileDataContext;
